import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  Row, 
  Col,
  Tabs,
  Table,
  message,
  Modal,
  Space,
  Divider,
  Statistic,
} from 'antd';
import { 
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import AuthGuard from '../components/AuthGuard';
import EnvironmentSwitcher from '../components/EnvironmentSwitcher';
import {
  getSystemConfig,
  updateSystemConfig,
  getProfitConfig,
  batchUpdateProfitConfig,
  updateAppVersion,
  getAppVersion,
} from '../services/adminApi';

const { Option } = Select;
const { TextArea } = Input;

interface SystemConfig {
  id: number;
  key: string;
  label: string;
  value: string | number | boolean;
  type: 'input' | 'select' | 'switch' | 'number' | 'textarea';
  description?: string;
  options?: { label: string; value: string | number }[];
  createdAt: string;
  updatedAt: string;
}

const SystemSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [profitForm] = Form.useForm();
  const [versionForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [systemConfigs, setSystemConfigs] = useState<SystemConfig[]>([]);
  const [profitConfigs, setProfitConfigs] = useState<Record<string, number>>({});
  const [appVersion, setAppVersion] = useState({
    version: '',
    appName: '',
    apiVersion: '',
  });
  const [announcements, setAnnouncements] = useState([
    {
      id: 1,
      title: '系统维护通知',
      content: '系统将于今晚22:00-24:00进行维护，期间服务可能中断。',
      type: 'maintenance',
      status: 'active',
      createdAt: '2024-12-20 10:00:00',
    },
    {
      id: 2,
      title: '新功能上线',
      content: '数据分析模块已上线，欢迎体验新的数据可视化功能。',
      type: 'feature',
      status: 'active',
      createdAt: '2024-12-19 15:30:00',
    },
  ]);

  // 获取系统配置
  const fetchSystemConfig = async () => {
    try {
      const response = await getSystemConfig();
      if (response.data.success) {
        setSystemConfigs(response.data.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取系统配置失败');
    }
  };

  // 获取分润配置
  const fetchProfitConfig = async () => {
    try {
      const response = await getProfitConfig();
      if (response.data.success) {
        setProfitConfigs(response.data.data);
        profitForm.setFieldsValue(response.data.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取分润配置失败');
    }
  };

  // 获取APP版本
  const fetchAppVersion = async () => {
    try {
      const response = await getAppVersion();
      if (response.data.success) {
        setAppVersion(response.data.data);
        versionForm.setFieldsValue(response.data.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取APP版本失败');
    }
  };

  useEffect(() => {
    fetchSystemConfig();
    fetchProfitConfig();
    fetchAppVersion();
  }, []);

  const handleSystemSave = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      // 这里简化处理，实际应该逐个更新配置
      message.success('系统设置保存成功');
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleProfitSave = async () => {
    setLoading(true);
    try {
      const values = await profitForm.validateFields();
      const response = await batchUpdateProfitConfig(values);
      
      if (response.data.success) {
        message.success('分润配置保存成功');
        fetchProfitConfig();
      }
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSave = async () => {
    setLoading(true);
    try {
      const values = await versionForm.validateFields();
      const response = await updateAppVersion(values);

      if (response.data.success) {
        message.success('APP版本更新成功');
        fetchAppVersion();
      }
    } catch (error: any) {
      message.error(error.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnnouncement = () => {
    message.info('添加公告功能开发中...');
  };

  const renderFormItem = (config: SystemConfig) => {
    const { key, label, value, type, description, options } = config;
    
    const commonProps = {
      label,
      key,
      name: key,
      initialValue: value,
      help: description,
    };

    switch (type) {
      case 'input':
        return <Input {...commonProps} />;
      case 'number':
        return <InputNumber {...commonProps} style={{ width: '100%' }} />;
      case 'select':
        return (
          <Select {...commonProps}>
            {options?.map((option: { label: string; value: string | number }) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );
      case 'switch':
        return <Switch {...commonProps} checkedChildren="开" unCheckedChildren="关" />;
      case 'textarea':
        return <TextArea {...commonProps} rows={4} />;
      default:
        return <Input {...commonProps} />;
    }
  };

  return (
    <AuthGuard>
      <div>
        <EnvironmentSwitcher />
        <Tabs 
          defaultActiveKey="profit" 
          type="card"
          items={[
            {
              key: 'profit',
              label: '分润配置',
              children: (
                <Card
                  title="分润参数配置"
                  extra={
                    <Space>
                      <Button 
                        icon={<ReloadOutlined />}
                        onClick={fetchProfitConfig}
                      >
                        重置
                      </Button>
                      <Button 
                        type="primary" 
                        icon={<SaveOutlined />}
                        loading={loading}
                        onClick={handleProfitSave}
                      >
                        保存设置
                      </Button>
                    </Space>
                  }
                >
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="平台费率"
                        value={(profitConfigs.PLATFORM_FEE_RATE || 0) * 100}
                        precision={2}
                        suffix="%"
                        prefix={<DollarOutlined />}
                        valueStyle={{ color: '#f5222d' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="直推分润比例"
                        value={(profitConfigs.DIRECT_REFERRAL_RATE || 0) * 100}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="团队管理提成"
                        value={(profitConfigs.TEAM_MANAGEMENT_RATE || 0) * 100}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="事业部激励"
                        value={(profitConfigs.TEAM_REWARD_RATE || 0) * 100}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Col>
                  </Row>
                  <Form
                    form={profitForm}
                    layout="vertical"
                  >
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          label="平台费率"
                          name="PLATFORM_FEE_RATE"
                          help="平台从总利润中抽取的比例（0-1之间的小数，例如：0.1表示10%）"
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.01}
                            placeholder="例如：0.1"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="直推分润比例"
                          name="DIRECT_REFERRAL_RATE"
                          help="一级推荐官获得的基础分润比例（0-1之间的小数，例如：0.8表示80%）"
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.01}
                            placeholder="例如：0.8"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="团队管理提成比例"
                          name="TEAM_MANAGEMENT_RATE"
                          help="二级、三级推荐官的团队管理提成比例（0-1之间的小数，例如：0.04表示4%）"
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.01}
                            placeholder="例如：0.04"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="事业部激励比例"
                          name="TEAM_REWARD_RATE"
                          help="三级推荐官的事业部激励比例（0-1之间的小数，例如：0.01表示1%）"
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.01}
                            placeholder="例如：0.01"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>
              ),
            },
            {
              key: 'version',
              label: 'APP版本管理',
              children: (
                <Card
                  title="APP版本配置"
                  extra={
                    <Space>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchAppVersion}
                      >
                        刷新
                      </Button>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={loading}
                        onClick={handleVersionSave}
                      >
                        保存设置
                      </Button>
                    </Space>
                  }
                >
                  <Form
                    form={versionForm}
                    layout="vertical"
                  >
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          label="APP版本号"
                          name="version"
                          rules={[{ required: true, message: '请输入APP版本号' }]}
                          help="APP版本号，例如：1.0.0"
                        >
                          <Input placeholder="请输入APP版本号" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="应用名称"
                          name="appName"
                          rules={[{ required: true, message: '请输入应用名称' }]}
                        >
                          <Input placeholder="请输入应用名称" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          label="API版本"
                          name="apiVersion"
                        >
                          <Input placeholder="请输入API版本" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div style={{ marginTop: 16 }}>
                      <div>当前版本信息：</div>
                      <div>版本号: {appVersion.version}</div>
                      <div>应用名称: {appVersion.appName}</div>
                      <div>API版本: {appVersion.apiVersion}</div>
                    </div>
                  </Form>
                </Card>
              ),
            },
          ]}
        />
      </div>
    </AuthGuard>
  );
};

export default SystemSettings;